import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, doc, getDoc, updateDoc, arrayUnion, query, orderBy } from 'firebase/firestore';
import { Gift, CheckCircle, ShieldCheck, User } from 'lucide-react';
import TeamCareerAccess from '../../components/admin/TeamCareerAccess.js';
import '../../styles/CareerPlanAdmin.css';

const CareerPlanAdmin = () => {
    const [usersProgress, setUsersProgress] = useState([]);
    const [ranks, setRanks] = useState({});
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('');

    useEffect(() => {
        const loadCareerData = async () => {
            try {
                // 1. Cargar configuración de rangos para saber qué premios tocan
                const ranksSnap = await getDocs(query(collection(db, 'ranks'), orderBy('pointsRequired', 'asc')));
                const ranksMap = {};
                ranksSnap.forEach(d => { ranksMap[d.id] = d.data(); });
                setRanks(ranksMap);

                // 2. Cargar progreso de todos los usuarios
                const usersSnap = await getDocs(collection(db, 'users'));
                const progressList = [];

                for (const userDoc of usersSnap.docs) {
                    const profileRef = doc(db, 'users', userDoc.id, 'profile', 'data');
                    const profileSnap = await getDoc(profileRef);

                    if (profileSnap.exists()) {
                        const profile = profileSnap.data();
                        if (profile.rankId) {
                            progressList.push({
                                uid: userDoc.id,
                                email: userDoc.data().email || 'Usuario sin email',
                                rankId: profile.rankId,
                                points: profile.totalPoints || 0,
                                validatedRewards: profile.validatedRewards || []
                            });
                        }
                    }
                }
                setUsersProgress(progressList);
            } catch (err) {
                console.error("Error al cargar Plan Carrera:", err);
            } finally {
                setLoading(false);
            }
        };
        loadCareerData();
    }, []);

    const validatePrize = async (userId, rankId) => {
        if (!window.confirm(`¿Confirmas que has entregado el premio de ${rankId.toUpperCase()} a este socio?`)) return;
        
        try {
            const ref = doc(db, 'users', userId, 'profile', 'data');
            await updateDoc(ref, {
                validatedRewards: arrayUnion(rankId)
            });
            setUsersProgress(prev => prev.map(u => 
                u.uid === userId ? { ...u, validatedRewards: [...u.validatedRewards, rankId] } : u
            ));
        } catch (e) {
            alert("Error al validar premio");
        }
    };

    const filtered = usersProgress.filter(u => u.email.toLowerCase().includes(filter.toLowerCase()));

    if (loading) return <div className="p-4">Cargando gestión de Recorrido Gerencial...</div>;

    return (
        <div className="career-admin-container">
            <div className="career-admin-header">
                <div className="title-group">
                    <ShieldCheck size={32} color="#2563eb" />
                    <h2>Gestión de Rangos y Premios</h2>
                </div>
                <div className="search-wrapper">
                    <input 
                        type="text" 
                        placeholder="Buscar socio por email..." 
                        onChange={(e) => setFilter(e.target.value)}
                    />
                </div>
            </div>

            <TeamCareerAccess />

            <div className="career-table-card">
                <table className="career-table">
                    <thead>
                        <tr>
                            <th>Socio</th>
                            <th>Rango Actual</th>
                            <th>Puntos</th>
                            <th>Premio</th>
                            <th>Estado Entrega</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map(user => {
                            const rankData = ranks[user.rankId];
                            const isDelivered = user.validatedRewards.includes(user.rankId);
                            return (
                                <tr key={user.uid}>
                                    <td><div className="user-info"><User size={14}/> {user.email}</div></td>
                                    <td><span className={`rank-badge ${user.rankId}`}>{rankData?.name || user.rankId}</span></td>
                                    <td className="font-bold">{user.points.toLocaleString()}</td>
                                    <td>{rankData?.prize?.join(', ') || 'N/A'}</td>
                                    <td>
                                        {isDelivered ? (
                                            <span className="status-delivered"><CheckCircle size={14}/> Entregado</span>
                                        ) : (
                                            <button className="btn-action-validate" onClick={() => validatePrize(user.uid, user.rankId)}>
                                                <Gift size={14}/> Validar Entrega
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default CareerPlanAdmin;